import React, { useMemo } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import KeyValuePairs from '@amzn/awsui-components-console/key-value-pairs';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Button from '@amzn/awsui-components-console/button';

import { UserIcon } from 'src/components/commonComponents/UserIcon';
import { formatRank, formatMergeStatus, extractEntities, extractField, formatEpochOrISO, ExecutionRawEntity } from 'src/pages/manageExecution/ManageExecutionUtils';

import ShowSnowStatus from './ShowSNOWStatus';

interface CycleDataTabProps {
	cycleId: string;
	workflowType: string;
	workflowDefinitionName?: string;
	prefetchedData?: { entities: ExecutionRawEntity[] } | null;
	isLoading?: boolean;
}

export default function CycleDataTab({ cycleId, workflowType, workflowDefinitionName, prefetchedData, isLoading }: CycleDataTabProps) {
	const raw = (prefetchedData?.entities?.[0] || null) as ExecutionRawEntity | null;

	if (isLoading) {
		return (
			<Box textAlign="center" padding="xxl">
				<Spinner size="large" />
			</Box>
		);
	}

	if (!raw) {
		return (
			<Box textAlign="center" padding="l">
				<Box variant="p">No cycle data available for this execution.</Box>
			</Box>
		);
	}

	const status = (extractField(raw, 'Status') as string) || '';
	const blocked = Number(extractField(raw, 'Blocked') ?? 0);
	const rank = extractField(raw, 'RunningTaskRank');
	const entities = useMemo(() => {
		const allEntities = (prefetchedData?.entities || []) as ExecutionRawEntity[];
		if (allEntities.length > 1) {
			return allEntities
				.map(entity => entity.EntityName as string)
				.filter(Boolean);
		}
		return extractEntities(raw as Record<string, unknown>, (raw.EntityData || {}) as Record<string, unknown>);
	}, [prefetchedData, raw]);
	const owner = (extractField(raw, 'Owner') || extractField(raw, 'startedBy') || '') as string;
	const creationTime = extractField(raw, 'CreationTime');
	const actualStartTime = extractField(raw, 'ActualStartTime');
	const actualCompletionTime = extractField(raw, 'ActualCompletionTime');
	const lastStatusUpdateTime = extractField(raw, 'LastStatusUpdateTime');
	const lastStatusUpdatedBy = (extractField(raw, 'LastStatusUpdatedBy') || '') as string;

	return (
		<Box>
			<SpaceBetween size="l">
				<Container
					header={
						<Header variant="h2">Execution Details for {raw.CycleId || cycleId}</Header>
					}
				>
					<KeyValuePairs
						columns={3}
						items={Object.entries({
							'Workflow Name': workflowDefinitionName || '',
							'Workflow Id': (
								<Button
									variant="inline-link"
									href={`#/viewWorkflow?workflowId=${workflowType}`}
									target="_blank"
								>
									{workflowType}
								</Button>
							),
							'Cycle Id': raw.CycleId || cycleId,
							'Entities': entities.length > 0 ? entities.join(', ') : '',
							'Running Task Rank': formatRank(rank),
							'Merge Status': formatMergeStatus(blocked),
							'Workflow Status': <ShowSnowStatus status={status} />,
							'Actual Start Time': formatEpochOrISO(actualStartTime),
							'Actual Completion Time': formatEpochOrISO(actualCompletionTime),
							'Started By': owner ? <UserIcon user={owner} size="medium" /> : '',
							'Creation Time': formatEpochOrISO(creationTime),
							'Last Update Time': formatEpochOrISO(lastStatusUpdateTime),
							'Last Updated By': lastStatusUpdatedBy ? <UserIcon user={lastStatusUpdatedBy} size="medium" /> : '',
						}).filter(([, value]) => value !== '' && value !== null && value !== undefined && value !== 'N/A')
						.map(([label, value]) => ({
							label,
							value,
						}))}

					/>
				</Container>
			</SpaceBetween>
		</Box>
	);
}
